import { usePitcher } from "features/utils/usePitcher";
import type { IRange } from "monaco-editor";
import { useCallback, useEffect, useRef, useState } from "react";

import { useEditorRouter } from "hooks/useEditorRouter";
import { executeVSCodeCommand } from "utils/vscode";

import type {
  MessageFromDevToolToPreview,
  MessageFromPreviewToDevTool,
} from "./types";

export const useChromeDevtool = ({
  isConnected,
  previewUrl,
  onPreviewMessage,
  sendMessageToPreview,
}: {
  isConnected: boolean;
  previewUrl: string;
  onPreviewMessage: (
    subscriber: (message: MessageFromPreviewToDevTool) => void,
  ) => () => void;
  sendMessageToPreview: (message: MessageFromDevToolToPreview) => void;
}) => {
  const router = useEditorRouter();
  const pitcher = usePitcher();
  const devtoolIframeRef = useRef<HTMLIFrameElement>(null);

  const [devtoolVisible, setDevToolVisibility] = useState<boolean>(
    router.queryParams.showConsole.get() || false,
  );
  const consoleMessageHistory = useRef<string[]>([]);
  const reloadDevtool = useCallback(() => {
    if (devtoolIframeRef.current && devtoolVisible) {
      devtoolIframeRef.current.contentWindow?.location.reload();

      // Clean message history
      consoleMessageHistory.current = [];
    }
  }, [devtoolVisible]);

  useEffect(
    function previewListener() {
      if (isConnected) {
        const unsubscribeDevtoolListener = onPreviewMessage((message) => {
          switch (message.type) {
            case "RELOAD":
              reloadDevtool();

              break;
          }
        });

        return () => {
          unsubscribeDevtoolListener();
        };
      }
    },
    [isConnected],
  );

  useEffect(
    function interceptProtocolMessages() {
      try {
        const listener = (
          event: MessageEvent<{ type: string; data: string } | string>,
        ) => {
          /**
           * Forward message from preview to chrome devtool
           */
          if (
            typeof event.data === "object" &&
            "type" in event.data &&
            event.data.type === "TO_DEVTOOL" &&
            devtoolIframeRef.current
          ) {
            devtoolIframeRef.current.contentWindow?.postMessage(
              event.data.data,
              "*",
            );
          }

          /**
           * Forward message from chrome devtool to preview
           */
          if (
            typeof event.data === "string" &&
            event.data.includes('"id"') &&
            isJson(event.data)
          ) {
            sendMessageToPreview({ type: "FROM_DEVTOOL", data: event.data });
          }

          /**
           * Protocol specific actions
           */
          const { method, data } = translateProtocolMessage(event.data);
          switch (method) {
            case "Runtime.consoleAPICalled": {
              consoleMessageHistory.current = [
                ...consoleMessageHistory.current,
                data,
              ];

              break;
            }

            /**
             * Once the chrome iframe enables console feature
             * we can forward messages from queue (dispatched before chrome iframe is ready)
             */
            case "Runtime.enable": {
              // TODO: remove setTimeout
              setTimeout(() => {
                consoleMessageHistory.current.forEach((data) => {
                  devtoolIframeRef.current?.contentWindow?.postMessage(
                    data,
                    "*",
                  );
                });
              }, 100);

              break;
            }

            case "Runtime.discardConsoleEntries": {
              consoleMessageHistory.current = [];

              break;
            }

            case "CSB.OpenFile": {
              let filepath = data
                .replace("/./", "/")
                .replace(previewUrl, "") as `$RELATIVE/${string}`;

              let initialSelections: IRange[] = [];
              if (filepath.includes(":")) {
                const [path, lineNumber, columnNumber] = filepath.split(":");
                filepath = path as `$RELATIVE/${string}`;

                initialSelections = [
                  {
                    startLineNumber: Number(lineNumber ?? 0),
                    endLineNumber: Number(lineNumber ?? 0),
                    startColumn: Number(columnNumber ?? 0),
                    endColumn: Number(columnNumber ?? 0),
                  },
                ];
              }

              const fileId = pitcher.clients.fs.getIdFromPath(filepath);
              if (fileId) {
                executeVSCodeCommand("codesandbox.openCsbTab", {
                  type: "FILE",
                  filepath,
                  initialSelections,
                });
              }

              break;
            }
          }
        };

        window.addEventListener("message", listener);

        return () => {
          window.removeEventListener("message", listener);
        };
      } catch (error) {
        /**
         * Errors like Out of memory ares expected to happen in devtools
         * when the payload is too heavy
         */

        /* eslint-disable-next-line */
        console.warn(error);
      }
    },
    [devtoolVisible],
  );

  const consoleErrors = consoleMessageHistory.current.filter((message) => {
    try {
      const json = JSON.parse(message);

      return (
        json.method === "Runtime.consoleAPICalled" &&
        json.params.type === "error"
      );
    } catch {
      return false;
    }
  });

  return {
    reloadDevtool,
    setDevToolVisibility,

    consoleErrors,
    devtoolVisible,
    devtoolIframeRef,
  };
};

const isJson = (value: string) => {
  try {
    JSON.parse(value);

    return true;
  } catch {
    return false;
  }
};

type Methods =
  | "Runtime.consoleAPICalled"
  | "Runtime.enable"
  | "Runtime.discardConsoleEntries"
  | "CSB.OpenFile"
  | "Unknown";

type Return = { method?: Methods; data: string };

const translateProtocolMessage = (
  data: string | { data: string; type: string; uri?: string },
): Return => {
  if (!data) {
    return { method: "Unknown", data: "" };
  }

  if (typeof data === "object" && data.type === "OPEN_TAB" && data.uri) {
    return { method: "CSB.OpenFile", data: data.uri };
  }

  const payload = typeof data === "string" ? data : data.data;

  if (isJson(payload)) {
    const json = JSON.parse(payload);

    return { method: json["method"] as Methods, data: payload };
  }

  return { data: payload };
};
